import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { AlertTriangle, ExternalLink } from 'lucide-react'
import type { PropertyCalendarOrphanGap, PropertyCalendarOrphanGapStage } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { formatCurrency } from '@/lib/format-utils'
import { formatGapDay, formatSnapshotTime, nightsLabel } from '@/lib/gap-events'
import { routes } from '@/lib/routes'
import { useRangeWords } from './range-words'

/**
 * What the calendar read says about the opened gap. `pending` is "not read
 * yet"; an `error` layer is said out loud and is never "no gap" (a property
 * that was never scanned has no gaps listed for the same reason a broken scan
 * does); `ok` with no gap means the snapshot has none starting on that night in
 * the month shown.
 */
export type GapLookup =
  | { status: 'pending' }
  | { status: 'error'; message: string; asOf: string | null }
  | {
      status: 'ok'
      gap: PropertyCalendarOrphanGap | null
      asOf: string
      caveats: string[]
    }

interface GapSheetProps {
  /** The gap's first night, `YYYY-MM-DD`: what `?open=gap:` names. */
  firstNight: string
  gap: GapLookup
  timeZone: string
  onClose: () => void
}

const STAGE_LABELS: Record<string, string> = {
  extend: 'Stay longer',
  arrive_earlier: 'Arrive earlier',
}

const STAGE_EXPLANATIONS: Record<string, string> = {
  extend: 'The guest leaving at the start of the gap stays on.',
  arrive_earlier: 'The guest arriving at the end of the gap comes sooner.',
}

const BASIS_LABELS: Record<string, string> = {
  paid: 'What they paid',
  market: 'Market rate',
}

/**
 * One orphan gap - empty nights between two bookings - opened from its hatched
 * bar on the property calendar.
 *
 * Everything shown is the stored scan's, as the calendar read returned it: the
 * nights, the suggested prices, who is eligible and why not, and the drafted
 * message. None of it is recomputed here, and **nothing here sends**: the
 * message is a read-only preview with no copy-and-send shortcut, because an
 * offer to a guest is reviewed and sent by a person, and sending is not built
 * yet. The one action is a link to the orphan offers page.
 *
 * The sheet always says when the scan ran: a gap list is a statement about a
 * moment, and "no longer there" and "not refreshed since Friday" look the same
 * on a grid.
 */
export function GapSheet({ firstNight, gap, timeZone, onClose }: GapSheetProps) {
  const item = gap.status === 'ok' ? gap.gap : null
  const words = useRangeWords()

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby="gap-sheet-description" data-testid="gap-sheet">
        <SheetHeader>
          <SheetTitle>Orphan gap</SheetTitle>
          <SheetDescription id="gap-sheet-description">
            {item ? describeSpan(item) : `Empty nights from ${formatGapDay(firstNight)}`}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-5">
          {gap.status === 'pending' ? (
            <div className="space-y-3" aria-busy="true">
              <LoadingSkeleton className="h-6 w-2/3" />
              <LoadingSkeleton className="h-32 w-full" />
            </div>
          ) : gap.status === 'error' ? (
            <div className="space-y-2" role="alert" data-testid="gap-sheet-error">
              <p className="text-sm text-danger">{gap.message}</p>
              <p className="text-sm text-textMuted">
                {gap.asOf
                  ? `The last scan that ran was ${formatSnapshotTime(gap.asOf, timeZone)}. `
                  : ''}
                This is not the same as there being no gaps.
              </p>
            </div>
          ) : (
            <>
              <SnapshotNotice asOf={gap.asOf} caveats={gap.caveats} timeZone={timeZone} />
              {item ? (
                <GapDetails gap={item} />
              ) : (
                <p className="text-sm text-textMuted" data-testid="gap-sheet-missing">
                  The latest scan lists no gap starting {formatGapDay(firstNight, { long: true })}{' '}
                  in {words.shown}. It may have been booked, or the scan may have moved on. The
                  orphan offers page has the current list.
                </p>
              )}
            </>
          )}
        </SheetBody>

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" size="sm" asChild>
            <Link to={routes.orphanOffers.path}>
              Open orphan offers
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function describeSpan(gap: PropertyCalendarOrphanGap): string {
  const first = formatGapDay(gap.first_night)
  const last = formatGapDay(gap.last_night)
  const span = gap.first_night === gap.last_night ? first : `${first} - ${last}`
  return `${span} · ${nightsLabel(gap.nights)}`
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">{children}</h3>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-sm text-textMuted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-text">{children}</dd>
    </div>
  )
}

/** Money through the shared formatter; the orphan offers page does the same (USD is its default). */
const money = (amount: number) => formatCurrency(amount)

/**
 * When the scan this answer comes from ran, in the property's zone, and - if it
 * is stale or cut short - that, in an amber note. Always shown.
 */
function SnapshotNotice({
  asOf,
  caveats,
  timeZone,
}: {
  asOf: string
  caveats: string[]
  timeZone: string
}) {
  return (
    <section aria-label="Snapshot" className="space-y-1.5" data-testid="gap-sheet-snapshot">
      <p className="text-sm text-textMuted">
        From the scan of{' '}
        <span className="font-medium text-text">{formatSnapshotTime(asOf, timeZone)}</span>. Gaps
        are checked once a day, so this is not live.
      </p>
      {caveats.map(caveat => (
        <p
          key={caveat}
          role="status"
          className="flex items-start gap-1.5 rounded border border-amber/40 bg-warningBg px-2.5 py-1.5 text-xs text-amber"
          data-testid="gap-sheet-caveat"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>{caveat}</span>
        </p>
      ))}
    </section>
  )
}

function GapDetails({ gap }: { gap: PropertyCalendarOrphanGap }) {
  const words = useRangeWords()
  return (
    <>
      <section aria-label="Nights" className="space-y-1.5">
        <SectionHeading>Nights</SectionHeading>
        <dl className="divide-y divide-border">
          <Fact label="First night">{formatGapDay(gap.first_night, { long: true })}</Fact>
          <Fact label="Last night">{formatGapDay(gap.last_night, { long: true })}</Fact>
          <Fact label="Length">{nightsLabel(gap.nights)}</Fact>
          <Fact label="Minimum stay">{nightsLabel(gap.min_stay)}</Fact>
          <Fact label="Offer">
            {gap.actionable ? (
              <span className="text-mint" data-testid="gap-sheet-actionable">
                A guest can be offered these nights
              </span>
            ) : (
              <span className="text-textMuted" data-testid="gap-sheet-actionable">
                No guest can be offered these nights right now
              </span>
            )}
          </Fact>
          {gap.actionable && <Fact label="Best offer value">{money(gap.offer_value)}</Fact>}
        </dl>
        {(gap.starts_before_range || gap.ends_after_range) && (
          <p className="text-xs text-textMuted" data-testid="gap-sheet-continues">
            {words.gapContinues(gap.starts_before_range, gap.ends_after_range)}
          </p>
        )}
      </section>

      {gap.stages.length === 0 ? (
        <p className="text-sm text-textMuted" data-testid="gap-sheet-no-stages">
          The scan did not assess any neighbouring guest for this gap.
        </p>
      ) : (
        gap.stages.map(stage => <StageSection key={stage.stage} stage={stage} />)
      )}
    </>
  )
}

/** What sits on the stage's side of the gap, in words. */
function neighbourText(stage: PropertyCalendarOrphanGapStage): string {
  switch (stage.neighbour.kind) {
    case 'reservation':
      return [stage.guest_name ?? 'A guest', stage.channel].filter(Boolean).join(' · ')
    case 'owner_block':
      return 'An owner block'
    case 'unbookable':
      return 'A night that cannot be booked'
    default:
      return stage.neighbour.kind
  }
}

function StageSection({ stage }: { stage: PropertyCalendarOrphanGapStage }) {
  const label = STAGE_LABELS[stage.stage] ?? stage.stage
  const explanation = STAGE_EXPLANATIONS[stage.stage]
  return (
    <section
      aria-label={label}
      className="space-y-2 border-t border-border pt-4"
      data-testid="gap-sheet-stage"
      data-stage={stage.stage}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SectionHeading>{label}</SectionHeading>
        <Badge variant={stage.eligible ? 'default' : 'secondary'}>
          {stage.eligible ? 'Eligible' : 'Not eligible'}
        </Badge>
      </div>
      <p className="text-sm text-text" data-testid="gap-sheet-neighbour">
        {neighbourText(stage)}
      </p>
      {explanation && <p className="text-xs text-textMuted">{explanation}</p>}

      {!stage.eligible ? (
        <p className="text-sm text-textMuted" data-testid="gap-sheet-reason">
          {stage.reason_label ?? stage.reason ?? 'No reason was given.'}
        </p>
      ) : (
        <>
          {stage.pricing ? (
            <StagePricing pricing={stage.pricing} />
          ) : (
            <p className="text-sm text-textMuted" data-testid="gap-sheet-no-pricing">
              No suggested prices were returned for this offer.
            </p>
          )}
          <DraftedMessage message={stage.message} />
        </>
      )}
    </section>
  )
}

function StagePricing({
  pricing,
}: {
  pricing: NonNullable<PropertyCalendarOrphanGapStage['pricing']>
}) {
  return (
    <div className="space-y-2" data-testid="gap-sheet-pricing">
      <dl className="divide-y divide-border">
        <Fact label="They paid">{money(pricing.paid_nightly)} / night</Fact>
        <Fact label="Rate applied">{Math.round(pricing.discount_rate * 100)}%</Fact>
        <Fact label="Offer value">{money(pricing.offer_value)}</Fact>
      </dl>
      {pricing.nights.length > 0 && (
        <table className="w-full text-sm">
          <caption className="sr-only">Suggested nightly prices</caption>
          <thead>
            <tr className="text-left text-xs text-textFaint">
              <th scope="col" className="py-1 pr-2 font-medium">
                Night
              </th>
              <th scope="col" className="py-1 pr-2 text-right font-medium">
                Market
              </th>
              <th scope="col" className="py-1 pr-2 text-right font-medium">
                Suggested
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Based on
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pricing.nights.map(night => (
              <tr key={night.date} data-testid="gap-sheet-night">
                <td className="whitespace-nowrap py-1.5 pr-2 tabular-nums text-text">
                  {formatGapDay(night.date)}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-textMuted">
                  {night.market_nightly === null ? 'None' : money(night.market_nightly)}
                </td>
                <td className="py-1.5 pr-2 text-right font-medium tabular-nums text-text">
                  {money(night.suggested_nightly)}
                </td>
                <td className="py-1.5 text-right text-xs text-textMuted">
                  {BASIS_LABELS[night.capped_by] ?? night.capped_by}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * The drafted offer, read-only. Plain text in a labelled block: not a field, no
 * copy button, nothing to send. An offer to a guest is reviewed by a person.
 */
function DraftedMessage({ message }: { message: string | null }) {
  return (
    <div className="space-y-1" data-testid="gap-sheet-message-section">
      <h4 className="text-xs font-medium text-textMuted">Drafted message</h4>
      {message ? (
        <>
          <blockquote
            className="whitespace-pre-wrap rounded border border-border bg-surface2 px-3 py-2 text-sm text-text"
            data-testid="gap-sheet-message"
          >
            {message}
          </blockquote>
          <p className="text-xs text-textFaint">
            Read-only preview. Offers are not sent from this screen.
          </p>
        </>
      ) : (
        <p className="text-sm text-textMuted" data-testid="gap-sheet-no-message">
          No message has been drafted for this offer.
        </p>
      )}
    </div>
  )
}
